import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { ClipboardList, CheckCircle, Clock, AlertCircle, RefreshCw, Play, Check } from "lucide-react";
import { toast } from "sonner";
import { API, AuthContext } from "../App";
import DraggableResizableWindow from "./DraggableResizableWindow";

const StaffTasksPanel = ({ onClose }) => {
  const { token, user } = useContext(AuthContext);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, pending, in_progress, completed

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/staff/my-tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [token]);

  const handleComplete = async (taskId) => {
    try {
      await axios.put(`${API}/staff/tasks/${taskId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Task marked as complete!");
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete task");
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case "urgent":
        return <span className="bg-red-600 text-white text-[10px] px-1 rounded animate-pulse">URGENT</span>;
      case "high":
        return <span className="bg-orange-500 text-white text-[10px] px-1 rounded">HIGH</span>;
      case "normal":
        return <span className="bg-blue-500 text-white text-[10px] px-1 rounded">NORMAL</span>;
      case "low":
        return <span className="bg-gray-400 text-white text-[10px] px-1 rounded">LOW</span>;
      default:
        return null;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "in_progress":
        return <Play className="w-4 h-4 text-blue-500" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === "all") return true;
    return task.status === filter;
  });

  const pendingCount = tasks.filter(t => t.status === "pending").length;
  const inProgressCount = tasks.filter(t => t.status === "in_progress").length;
  const completedCount = tasks.filter(t => t.status === "completed").length;

  return (
    <DraggableResizableWindow
      title="My Tasks"
      onClose={onClose}
      initialWidth={500}
      initialHeight={450}
      minWidth={350}
      minHeight={300}
    >
      <div className="flex flex-col h-full bg-[#C0C0C0]" data-testid="staff-tasks-panel">
        {/* Toolbar */}
        <div className="flex items-center gap-2 p-2 border-b border-[#808080] flex-wrap">
          <button
            onClick={() => setFilter("all")}
            className={`retro-button text-xs px-2 py-1 ${filter === "all" ? "bg-purple-500 text-white" : ""}`}
          >
            All ({tasks.length})
          </button>
          <button
            onClick={() => setFilter("pending")}
            className={`retro-button text-xs px-2 py-1 ${filter === "pending" ? "bg-yellow-500 text-white" : ""}`}
          >
            Pending ({pendingCount})
          </button>
          <button
            onClick={() => setFilter("in_progress")}
            className={`retro-button text-xs px-2 py-1 ${filter === "in_progress" ? "bg-blue-500 text-white" : ""}`}
          >
            In Progress ({inProgressCount})
          </button>
          <button
            onClick={() => setFilter("completed")}
            className={`retro-button text-xs px-2 py-1 ${filter === "completed" ? "bg-green-500 text-white" : ""}`}
          >
            Done ({completedCount})
          </button>
          <button
            onClick={fetchTasks}
            className="retro-button text-xs px-2 py-1 ml-auto"
            title="Refresh"
          >
            <RefreshCw className="w-3 h-3" />
          </button>
        </div>

        {/* Tasks List */}
        <div className="flex-1 overflow-y-auto retro-scrollbar p-2">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm">Loading tasks...</p>
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>{filter === "all" ? "No tasks assigned to you" : `No ${filter.replace('_', ' ')} tasks`}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredTasks.map(task => (
                <div
                  key={task.task_id}
                  className={`retro-window p-3 ${
                    task.status === "completed" ? "bg-green-50 opacity-70" :
                    task.priority === "urgent" ? "bg-red-50 border-red-300" :
                    task.priority === "high" ? "bg-orange-50" :
                    "bg-white"
                  }`}
                  data-testid={`task-item-${task.task_id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 flex-1">
                      {getStatusIcon(task.status)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-bold text-sm ${task.status === "completed" ? "line-through text-gray-500" : ""}`}>
                            {task.title}
                          </span>
                          {getPriorityBadge(task.priority)}
                        </div>
                        {task.description && (
                          <p className="text-xs text-gray-600 mt-1">{task.description}</p>
                        )}
                        <div className="text-[10px] text-gray-400 mt-2">
                          Assigned by: {task.created_by} • {new Date(task.created_at).toLocaleDateString()}
                          {task.completed_at && (
                            <span className="ml-2 text-green-600">
                              Completed: {new Date(task.completed_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Button */}
                    {task.status !== "completed" && (
                      <button
                        onClick={() => handleComplete(task.task_id)}
                        className="retro-button text-xs px-3 py-1 bg-green-500 text-white flex items-center gap-1"
                        data-testid={`complete-task-${task.task_id}`}
                      >
                        <Check className="w-3 h-3" />
                        Done
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Summary Footer */}
        <div className="p-2 border-t border-[#808080] bg-[#E0E0E0]">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">
              {pendingCount + inProgressCount > 0 ? (
                <>
                  <AlertCircle className="w-3 h-3 inline mr-1 text-yellow-500" />
                  {pendingCount + inProgressCount} task(s) need attention
                </>
              ) : (
                <>
                  <CheckCircle className="w-3 h-3 inline mr-1 text-green-500" />
                  All tasks completed!
                </>
              )}
            </span>
            <span className="text-gray-500">
              Total: {tasks.length} tasks
            </span>
          </div>
        </div>
      </div>
    </DraggableResizableWindow>
  );
};

export default StaffTasksPanel;
